import { useState } from 'react';
import { Spinner, useConfirm } from '@busbook/ui';
import { Page } from '../components/Shell.js';
import { Icon, ICONS } from '../components/Icon.js';
import { api } from '../lib/api.js';
import { useResource } from '../lib/useResource.js';
import { useT } from '../i18n/index.js';

/**
 * The demo estate and its credentials (the "credentials table" the owner
 * demonstrates from).
 *
 * The password is printed in the open, deliberately. These accounts exist only on
 * sandbox, dev and UAT — the API refuses to create them anywhere else — and a
 * demo login nobody can read is not a demo login. The banner says so plainly
 * rather than leaving a reader to wonder whether this is a leak.
 */

interface DemoStaff {
  role: string;
  email: string;
}

interface DemoOperator {
  id: string;
  slug: string;
  name: string;
  country: string;
  currency: string;
  timezone: string;
  passengerUrl: string;
  adminUrl: string;
  staff: DemoStaff[];
}

interface DemoState {
  available: boolean;
  refusal: string | null;
  password: string | null;
  operators: DemoOperator[];
}

/** Copy-to-clipboard that admits when the browser refused. */
function Copy({ value, label }: { value: string; label: string }) {
  const t = useT();
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1600);
        } catch {
          /* clipboard denied (insecure origin, or the user said no) — the value
             is on screen and selectable, so there is nothing to recover from */
        }
      }}
      className="rounded-md border px-2 py-0.5 text-[0.6875rem] font-medium transition-colors"
      style={{
        borderColor: copied ? 'var(--signal-line)' : 'var(--hairline)',
        color: copied ? 'var(--signal)' : 'var(--chalk-faint)',
      }}
      aria-label={`${t('demo.copy')} ${label}`}
    >
      {copied ? t('demo.copied') : t('demo.copy')}
    </button>
  );
}

function LinkOut({ href, children }: { href: string; children: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1 text-[0.75rem] underline-offset-2 hover:underline"
      style={{ color: 'var(--chalk-dim)' }}
    >
      {children}
      <Icon d={ICONS.external} className="h-3 w-3" />
    </a>
  );
}

export function Demo() {
  const t = useT();
  const demo = useResource<DemoState>('/platform/demo');
  const [busy, setBusy] = useState<'seed' | 'clear' | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { ask, dialog } = useConfirm();

  async function run(action: 'seed' | 'clear') {
    setBusy(action);
    setError(null);
    setNotice(null);
    try {
      if (action === 'seed') {
        const result = await api.post<{ operators: unknown[] }>('/platform/demo/seed');
        setNotice(t('demo.seeded', { count: result.operators.length }));
      } else {
        const result = await api.delete<{ removed: number }>('/platform/demo');
        setNotice(t('demo.cleared', { count: result.removed }));
      }
      await demo.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  if (demo.loading) {
    return (
      <Page title={t('demo.title')}>
        <div className="grid place-items-center py-20">
          <Spinner label={t('common.loading')} />
        </div>
      </Page>
    );
  }
  if (!demo.data) {
    return (
      <Page title={t('demo.title')}>
        <p style={{ color: 'var(--halt)' }}>{demo.error}</p>
      </Page>
    );
  }

  const data = demo.data;
  const seeded = data.operators.length > 0;

  return (
    <Page title={t('demo.title')} description={t('demo.subtitle')}>
      {/* The refusal is shown as fact, not as an error: a live deployment
          declining to seed demo accounts is the system working. */}
      {!data.available && (
        <div
          className="rounded-xl border p-4"
          style={{ borderColor: 'var(--hairline)', background: 'var(--ink-850)' }}
        >
          <p className="text-[0.8125rem] font-semibold">{t('demo.unavailable')}</p>
          <p className="mt-1 text-[0.8125rem]" style={{ color: 'var(--chalk-dim)' }}>
            {data.refusal}
          </p>
        </div>
      )}

      {data.available && (
        <>
          <p
            className="rounded-xl border p-3 text-[0.75rem]"
            style={{
              borderColor: 'var(--signal-line)',
              background: 'var(--ink-850)',
              color: 'var(--chalk-dim)',
            }}
          >
            {t('demo.warning')}
          </p>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={busy !== null}
              onClick={() => {
                void (async () => {
                  // Seeding a clean estate needs no question; re-seeding one replaces what is there.
                  if (seeded && !(await ask({
                    title: t('demo.reseed'),
                    body: t('demo.confirmReseed'),
                    confirmLabel: t('demo.reseed'),
                  }))) return;
                  await run('seed');
                })();
              }}
              className="rounded-lg px-3 py-1.5 text-[0.8125rem] font-semibold disabled:opacity-50"
              style={{ background: 'var(--signal)', color: 'var(--ink-900)' }}
            >
              {busy === 'seed' ? t('demo.seeding') : seeded ? t('demo.reseed') : t('demo.seed')}
            </button>
            {seeded && (
              <button
                type="button"
                disabled={busy !== null}
                onClick={() => {
                  void (async () => {
                    if (await ask({
                      title: t('demo.clear'),
                      body: t('demo.confirmClear'),
                      confirmLabel: t('demo.clear'),
                    })) await run('clear');
                  })();
                }}
                className="rounded-lg border px-3 py-1.5 text-[0.8125rem] font-semibold disabled:opacity-50"
                style={{ borderColor: 'var(--hairline)', color: 'var(--halt)' }}
              >
                {busy === 'clear' ? t('demo.clearing') : t('demo.clear')}
              </button>
            )}
            {notice && (
              <span className="text-[0.8125rem]" style={{ color: 'var(--signal)' }}>
                {notice}
              </span>
            )}
            {error && (
              <span className="text-[0.8125rem]" style={{ color: 'var(--halt)' }}>
                {error}
              </span>
            )}
          </div>

          {data.password && (
            <div
              className="mt-4 flex flex-wrap items-center gap-3 rounded-xl border p-4"
              style={{ borderColor: 'var(--hairline)', background: 'var(--ink-850)' }}
            >
              <div>
                <p
                  className="text-[0.625rem] font-semibold uppercase tracking-[0.18em]"
                  style={{ color: 'var(--chalk-faint)' }}
                >
                  {t('demo.password')}
                </p>
                <p className="font-mono mt-1 text-[1rem]" style={{ color: 'var(--signal)' }}>
                  {data.password}
                </p>
              </div>
              <Copy value={data.password} label={t('demo.password')} />
              <p className="text-[0.75rem]" style={{ color: 'var(--chalk-faint)' }}>
                {t('demo.passwordHint')}
              </p>
            </div>
          )}
        </>
      )}

      {!seeded ? (
        <div
          className="mt-4 rounded-xl border px-5 py-10 text-center"
          style={{ background: 'var(--ink-850)', borderColor: 'var(--hairline)' }}
        >
          <p className="text-[0.875rem] font-medium">{t('demo.empty')}</p>
          <p className="mt-1 text-[0.8125rem]" style={{ color: 'var(--chalk-faint)' }}>
            {t('demo.emptyBody')}
          </p>
        </div>
      ) : (
        <div className="mt-4 flex flex-col gap-3">
          {data.operators.map((operator) => (
            <section
              key={operator.id}
              className="overflow-hidden rounded-xl border"
              style={{ background: 'var(--ink-850)', borderColor: 'var(--hairline)' }}
            >
              <header
                className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-2 border-b px-4 py-3"
                style={{ borderColor: 'var(--hairline)' }}
              >
                <div>
                  <h2 className="font-display text-[0.9375rem] font-bold">{operator.name}</h2>
                  <p className="text-[0.75rem]" style={{ color: 'var(--chalk-faint)' }}>
                    {operator.country} · {operator.currency} · {operator.timezone}
                  </p>
                </div>
                <div className="flex flex-wrap gap-4">
                  <LinkOut href={operator.passengerUrl}>{t('demo.passengerApp')}</LinkOut>
                  <LinkOut href={operator.adminUrl}>{t('demo.adminApp')}</LinkOut>
                </div>
              </header>

              {/* Fixed layout: each operator is its own table, and auto widths made
                  the "Sign in as" column land in a different place on every card. */}
              <table className="w-full table-fixed border-collapse text-[0.8125rem]">
                <colgroup>
                  <col style={{ width: '9rem' }} />
                  <col />
                  <col style={{ width: '5rem' }} />
                </colgroup>
                <thead>
                  <tr>
                    {[t('demo.colRole'), t('demo.colEmail'), ''].map((header, index) => (
                      <th
                        key={header || index}
                        className="border-b px-4 py-2 text-left text-[0.625rem] font-semibold uppercase tracking-[0.16em]"
                        style={{ borderColor: 'var(--hairline)', color: 'var(--chalk-faint)' }}
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {operator.staff.map((member) => (
                    <tr key={member.email}>
                      <td className="border-b px-4 py-2.5" style={{ borderColor: 'var(--hairline)' }}>
                        {member.role}
                      </td>
                      <td
                        className="font-mono border-b px-4 py-2.5"
                        style={{ borderColor: 'var(--hairline)' }}
                      >
                        {member.email}
                      </td>
                      <td
                        className="border-b px-4 py-2.5 text-right"
                        style={{ borderColor: 'var(--hairline)' }}
                      >
                        <Copy value={member.email} label={member.email} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          ))}
        </div>
      )}
      {dialog}
    </Page>
  );
}
